import React, { memo, useCallback } from 'react';
import { Card } from 'components/ui/card';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { cn } from 'lib/utils';
import { useNavigate } from 'react-router-dom';
import { Send, MessageCircle, Phone, ChevronLeft } from 'lucide-react';
import Logo from 'assets/images/logo.png';
import { AuthType } from '@app/models';
import { signUpByService } from '@app/api';

function RegisterByService() {
  const [step, setStep] = React.useState<'initial' | 'telegram'>('initial');
  const [serviceType, setServiceType] = React.useState<
    'telegram' | 'whatsapp' | 'sms' | null
  >(null);
  const [phone, setPhone] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);
  const navigate = useNavigate();

  const handlePhoneChange = (value: string) => {
    setPhone(formatPhoneNumber(value));
    setError(null);
  };

  const onClickBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const handleSubmit = async () => {
    if (!isFormValid()) return;
    try {
      if (serviceType === 'telegram') {
        await signUpByService({
          phone: phone.replace(/[^0-9]/g, ''),
          authType: AuthType.TELEGRAM,
        });
        setStep('telegram');
      }
    } catch (err: any) {
      setError(
        err.response?.data?.message || 'Произошла ошибка при регистрации'
      );
    }
  };

  const isFormValid = () => {
    return serviceType !== null && phone.replace(/\D/g, '').length === 10;
  };

  const getTelegramLink = () => {
    const cleanPhone = phone.replace(/[^0-9]/g, '');
    const botName =
      process.env.REACT_APP_TELEGRAM_BOT_NAME || 'smrhub_app_test_bot';

    return `https://t.me/${botName}?start=7${cleanPhone}-web`;
  };

  const formatPhoneNumber = (input: string) => {
    const digits = input.replace(/\D/g, '').substring(0, 10);
    const match = digits.match(/^(\d{0,3})(\d{0,3})(\d{0,2})(\d{0,2})$/);
    if (!match) return '';
    return [
      match[1] ? `(${match[1]}` : '',
      match[2] ? `) ${match[2]}` : '',
      match[3] ? ` ${match[3]}` : '',
      match[4] ? ` ${match[4]}` : '',
    ].join('');
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100">
      <Card className="w-full max-w-xl p-6 md:p-10 bg-white flex flex-col gap-6 relative">
        <div className="flex items-center gap-3 mb-2">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="rounded-full w-8 h-8 shrink-0"
            onClick={onClickBack}
            aria-label="Назад"
          >
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <img src={Logo} alt="SMR Logo" className="w-8 h-10 shrink-0" />
          <div className="flex flex-col">
            <h2 className="font-medium text-lg text-gray-900 m-0 p-0 leading-none">
              SMR
            </h2>
            <p className="text-xs text-muted-foreground m-0 p-0 leading-none">
              Поиск подряда и поставщиков
            </p>
          </div>
        </div>
        <h1 className="text-2xl font-bold text-center mb-2">Регистрация</h1>
        {step === 'initial' ? (
          <>
            <div className="mb-4">
              <Label htmlFor="phone" className="block text-sm font-medium mb-1">
                Номер телефона
              </Label>
              <Input
                id="phone"
                type="text"
                value={phone}
                onChange={(e) => handlePhoneChange(e.target.value)}
                placeholder="(777) 777 77 77"
                className={cn(error && 'border-red-500')}
                autoComplete="off"
                maxLength={20}
              />
              {error && (
                <div className="text-xs text-red-500 mt-1">{error}</div>
              )}
            </div>
            <h2 className="text-lg font-medium mb-2">
              Выберите тип авторизации
            </h2>

            <div className="grid grid-cols-1 xs:grid-cols-3 gap-3 mb-6">
              <Button
                type="button"
                variant="outline"
                className={cn(
                  'w-full flex items-center gap-2 justify-center truncate transition-all',
                  serviceType === 'telegram'
                    ? 'border-2 !border-[#0088cc] !bg-[#0088cc]/10 !text-[#0088cc] hover:!bg-[#0088cc]/15 shadow-sm'
                    : 'hover:border-gray-300'
                )}
                onClick={() => setServiceType('telegram')}
              >
                <Send className="shrink-0 w-5 h-5" />
                <span className="truncate font-medium">Telegram</span>
              </Button>

              <Button
                type="button"
                variant="outline"
                className={cn(
                  'w-full flex items-center gap-2 justify-center truncate transition-all',
                  serviceType === 'whatsapp'
                    ? 'border-2 !border-[#25d366] !bg-[#25d366]/10 !text-[#25d366] hover:!bg-[#25d366]/15 shadow-sm'
                    : 'hover:border-gray-300'
                )}
                onClick={() => setServiceType('whatsapp')}
              >
                <MessageCircle className="shrink-0 w-5 h-5" />
                <span className="truncate font-medium">WhatsApp</span>
              </Button>

              <Button
                type="button"
                variant="outline"
                className={cn(
                  'w-full flex items-center gap-2 justify-center truncate transition-all',
                  serviceType === 'sms'
                    ? 'border-2 !border-[#f97316] !bg-[#f97316]/10 !text-[#ea580c] hover:!bg-[#f97316]/15 shadow-sm'
                    : 'hover:border-gray-300'
                )}
                onClick={() => setServiceType('sms')}
              >
                <Phone className="shrink-0 w-5 h-5" />
                <span className="truncate font-medium">SMS</span>
              </Button>
            </div>

            <Button
              onClick={handleSubmit}
              disabled={!isFormValid()}
              className="w-full"
            >
              Продолжить
            </Button>
          </>
        ) : (
          <>
            <h2 className="text-lg font-medium mb-4 text-center">
              Для завершения регистрации подтвердите свой аккаунт в Telegram
            </h2>
            <a
              href={getTelegramLink()}
              target="_blank"
              rel="noopener noreferrer"
              className="block w-full p-3 rounded-md bg-[#27a7e7] text-white text-center font-medium hover:bg-[#229ad6] transition-colors"
            >
              Подтвердить Telegram
            </a>
          </>
        )}
      </Card>
    </div>
  );
}

export default memo(RegisterByService);
